import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Phone } from "lucide-react";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { ShareButton } from "../components/ShareButton";
import { ShareMenu } from "../components/ShareMenu";
import { useSocialMeta } from "../lib/socialMeta";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl:"https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png", iconUrl:"https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png", shadowUrl:"https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png"});

export default function EventDetail({ idOverride }) {
  const { t, i18n } = useTranslation();
  const routeParams = useParams();
  const navigate = useNavigate();
  const id = idOverride || routeParams.id;
  const u = getUser();
  const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
  const [e, setE] = useState(null);
  const [err, setErr] = useState(null);
  const [pwGate, setPwGate] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState(null); // { sid } → riepilogo pre-checkout evento a pagamento
  const eur = (c) => ((c || 0) / 100).toLocaleString(i18n.language, { style: "currency", currency: "EUR" });
  const load = () => {
    const saved = sessionStorage.getItem(`evpw_${id}`) || "";
    const qs = new URLSearchParams();
    if (saved) qs.set("pwd", saved);
    if (isPreview) qs.set("preview", "1");  // §F2: l'organizzatore vede la vera esperienza del visitatore (incl. gate password)
    const q = qs.toString();
    api.get(`/events/${id}${q ? `?${q}` : ""}`)
      .then(({data}) => { setE(data); setErr(null); setPwGate(false); })
      .catch((er) => {
        if (er.response?.status === 403 && er.response?.data?.detail === "event_password_required") { setPwGate(true); }
        else setErr(t("events.not_available"));
      });
  };
  const submitPw = async (ev) => {
    ev.preventDefault();
    try {
      const { data } = await api.get(`/events/${id}?pwd=${encodeURIComponent(pw)}${isPreview ? "&preview=1" : ""}`);
      sessionStorage.setItem(`evpw_${id}`, pw); setE(data); setPwGate(false);
    } catch { toast.error({it:"Password errata",en:"Wrong password",de:"Falsches Passwort",fr:"Mot de passe incorrect"}[i18n.language]||"Password errata"); }
  };
  useEffect(() => { load(); }, [id]);
  const [organizer, setOrganizer] = useState(null);
  useEffect(() => {
    if (!e?.organizer_id) return;
    api.get(`/organizations/${e.organizer_id}`).then(({data}) => setOrganizer(data.org)).catch(() => setOrganizer(null));
  }, [e?.organizer_id]);
  useSocialMeta(e ? {
    title: organizer?.org_name || "YouPAM",
    description: e.title || "Evento su YouPAM",
    image: organizer?.avatar_url || organizer?.cover_url,
    url: `${window.location.origin}/events/${id}`,
    type: "article",
  } : null);
  // Non autenticato: mantiene il contesto dell'evento dopo il login (ritorno alla stessa pagina).
  const requireLogin = () => navigate(`/login?next=${encodeURIComponent(`/events/${id}`)}`);
  const reg = async (sid) => {
    if (!u) { requireLogin(); return; }
    try {
      const {data} = await api.post(`/events/${id}/slots/${sid}/register`);
      if (data.requires_payment) { setConfirm({ sid }); return; }  // evento a pagamento → riepilogo prima del checkout
      toast.success(`${t("events.reg_success")} (${data.status === "confirmed" ? t("events.reg_confirmed") : t("events.waitlist")})`);
      load();
    } catch(err){ toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("events.reg_failed")); }
  };
  const startCheckout = async (sid) => {
    try {
      const {data: co} = await api.post(`/events/${id}/slots/${sid}/create-checkout`, { origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch(err){ setConfirm(null); toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("events.reg_failed")); }
  };
  const acceptOffer = (sid) => { if (!u) { requireLogin(); return; } setConfirm({ sid, offer: true }); };
  const cancel = async (sid) => { try { await api.post(`/events/${id}/slots/${sid}/cancel`); toast.success(t("events.cancel_success")); load(); } catch(err){ toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("events.cancel_failed")); } };
  const [csvBusy, setCsvBusy] = useState(false);
  const [regs, setRegs] = useState(null);
  const openRegs = async () => {
    try { const { data } = await api.get(`/events/${id}/registrations.json`); setRegs(data); }
    catch (err) { toast.error(err.response?.status === 403 ? t("events.csv_forbidden") : t("events.error_generic")); }
  };
  const dlCsv = async () => {
    setCsvBusy(true);
    try {
      const { data, headers } = await api.get(`/events/${id}/registrations.csv`, { responseType: "blob" });
      const m = /filename="([^"]+)"/.exec(headers["content-disposition"] || "");
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : `iscritti_${id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err.response?.status === 403 ? t("events.csv_forbidden") : t("events.error_generic")); }
    finally { setCsvBusy(false); }
  };
  if (pwGate) return (<Layout><div className="yp-container max-w-md py-12">
    <form onSubmit={submitPw} className="yp-card p-6 space-y-4" data-testid="event-password-gate">
      <h1 className="text-2xl font-bold">{{it:"Evento privato",en:"Private event",de:"Privates Event",fr:"Événement privé"}[i18n.language]||"Evento privato"}</h1>
      <p className="text-sm text-slate-600">{{it:"Questo evento è protetto da password.",en:"This event is password protected.",de:"Dieses Event ist passwortgeschützt.",fr:"Cet événement est protégé par mot de passe."}[i18n.language]}</p>
      <input className="yp-input" type="password" autoFocus value={pw} onChange={e=>setPw(e.target.value)} placeholder="Password" data-testid="event-password-input" />
      <button type="submit" className="yp-btn yp-btn-primary w-full" data-testid="event-password-submit">{{it:"Accedi all'evento",en:"Access event",de:"Zum Event",fr:"Accéder à l'événement"}[i18n.language]||"Accedi all'evento"}</button>
    </form>
  </div></Layout>);
  if (err) return <Layout><div className="yp-container py-8 text-red-600" data-testid="event-error">{err}</div></Layout>;
  if (!e) return <Layout><div className="yp-container py-8" data-testid="event-loading">{t("events.loading")}</div></Layout>;
  const isOrganizer = u?.id === e.organizer_id;
  const isCancelled = e.status === "cancelled";
  const priceLabel = e.is_paid ? eur(e.price_cents) : t("events.free");
  return (<Layout><div className="yp-container max-w-4xl py-8">
    {isCancelled && <div className="yp-card p-4 mb-4 border border-red-300 bg-red-50 text-red-700 text-sm font-medium" data-testid="event-cancelled-banner">{t("events.cancelled_banner")}</div>}
    <div className="yp-card p-6 yp-gradient-border">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">{e.title}</h1>
        <ShareMenu shareUrl={e.public_url || `${window.location.origin}/p/evento-${id}`} title={e.title}
          youpam={isOrganizer ? { entityType: "event", entityId: id, orgId: e.organizer_id } : undefined} />
      </div>
      <div className="mt-3">
        <span className={`yp-badge ${e.is_paid ? "yp-badge-paid" : "yp-badge-approved"}`} data-testid="event-price-badge">
          {t("events.price_label")}: {priceLabel}
        </span>
      </div>
      {e.image_url && (
        <div className="mt-4 w-full rounded-lg overflow-hidden bg-slate-100 border border-slate-200" data-testid="event-flyer">
          <img src={e.image_url.startsWith("http") ? e.image_url : `${process.env.REACT_APP_BACKEND_URL}${e.image_url}`} alt={e.title} className="w-full max-h-[420px] object-cover" />
        </div>)}
      <p className="mt-3 text-slate-700 whitespace-pre-wrap">{e.description}</p>
      {isOrganizer && e.visibility === "private" && (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="event-private-link-box">
          <div className="font-medium text-amber-800">{{it:"Evento privato — solo tramite link",en:"Private event — link only",de:"Privates Event — nur per Link",fr:"Événement privé — via lien"}[i18n.language]}{e.has_password ? " 🔒" : ""}</div>
          <div className="flex items-center gap-2 mt-1">
            <input readOnly className="yp-input !py-1 text-xs flex-1" value={`${window.location.origin}/events/${id}`} data-testid="event-private-link" onFocus={ev=>ev.target.select()} />
            <button type="button" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="event-copy-link" onClick={()=>{navigator.clipboard.writeText(`${window.location.origin}/events/${id}`); toast.success({it:"Link copiato",en:"Link copied",de:"Link kopiert",fr:"Lien copié"}[i18n.language]||"Link copiato");}}>{{it:"Copia link",en:"Copy link",de:"Link kopieren",fr:"Copier le lien"}[i18n.language]||"Copia link"}</button>
          </div>
        </div>)}
      <div className="mt-4 text-sm text-slate-600">📍 {e.address}</div>
      <div className="mt-4 h-64 rounded overflow-hidden">
        <MapContainer center={[e.lat||45.4642, e.lng||9.19]} zoom={13} style={{ height:"100%", width:"100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <Marker position={[e.lat||45.4642, e.lng||9.19]} />
        </MapContainer>
      </div>
    </div>
    <div className="mt-6">
      <h2 className="text-xl font-bold mb-3">{t("events.slots")}</h2>
      <div className="space-y-2">
        {(e.slots||[]).map(s => (
          <div key={s.id} className="yp-card p-4 flex justify-between items-center gap-3 flex-wrap" data-testid="event-slot">
            <div>
              <div className="font-medium">{new Date(s.start_at).toLocaleString(i18n.language)}</div>
              <div className="text-xs font-semibold text-slate-700 mt-0.5" data-testid="event-slot-price">{priceLabel}</div>
              <div className="text-xs text-slate-500">{isOrganizer
                ? `${t("events.capacity")} ${s.registered}/${s.capacity} · ${t("events.waitlist")} ${s.waitlist}`
                : (s.available ? t("events.avail_open") : t("events.avail_full"))}</div>
              {s.i_am_offered && <div className="text-xs text-amber-600 font-medium mt-1" data-testid="event-offer-note">{t("events.offer_active", { time: new Date(s.offer_expires_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" }) })}</div>}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {s.i_am_offered && !isCancelled && <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={()=>acceptOffer(s.id)} data-testid="event-offer-pay-btn">{t("events.offer_pay_now")}</button>}
              {(!u || u.role === "user") && !isCancelled && !s.closed && !s.i_am_registered && s.available !== false && (
                <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={()=>reg(s.id)} data-testid="event-card-rsvp-btn">
                  {e.is_paid ? t("events.pay_and_register", { price: eur(e.price_cents) }) : t("events.register")}
                </button>)}
              {u?.role === "user" && s.i_am_registered && !s.i_am_offered && <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={()=>cancel(s.id)} data-testid="event-slot-cancel">{t("events.cancel_reg")}</button>}
              {s.i_am_registered && !s.i_am_offered && <span className="yp-badge yp-badge-approved">{t("events.registered_badge")}</span>}
              {s.closed && <span className="text-xs text-red-500">{t("events.closed")}</span>}
            </div>
          </div>))}
      </div>
      {isOrganizer && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button className="yp-btn yp-btn-primary" onClick={openRegs} data-testid="event-show-attendees-btn">{t("att.show")}</button>
          <button className="yp-btn yp-btn-outline" onClick={dlCsv} disabled={csvBusy} data-testid="event-download-csv-btn">📥 {csvBusy ? t("common.loading") : t("events.csv_download")}</button>
        </div>)}
    </div>
    {isOrganizer && regs !== null && <AttendeesModal regs={regs} t={t} lang={i18n.language} onClose={() => setRegs(null)} />}
    {confirm && (
      <div className="fixed inset-0 z-[1200] bg-black/50 flex items-center justify-center p-4" data-testid="event-checkout-summary" onClick={()=>setConfirm(null)}>
        <div className="yp-card p-6 max-w-sm w-full bg-white" onClick={ev=>ev.stopPropagation()}>
          <h3 className="text-lg font-bold mb-3">{t("events.summary_title")}</h3>
          <div className="text-sm text-slate-700 font-medium">{e.title}</div>
          {(() => { const s = (e.slots||[]).find(x=>x.id===confirm.sid); return s ? (
            <div className="text-xs text-slate-500 mt-1" data-testid="summary-when">{t("events.summary_when")}: {new Date(s.start_at).toLocaleString(i18n.language)}</div>) : null; })()}
          <div className="text-sm mt-2" data-testid="summary-price">{t("events.summary_price")}: <span className="font-semibold">{eur(e.price_cents)}</span></div>
          <div className="flex gap-2 mt-5">
            <button className="yp-btn yp-btn-primary flex-1 !py-2 text-sm" onClick={()=>startCheckout(confirm.sid)} data-testid="summary-pay-btn">{t("events.summary_pay")}</button>
            <button className="yp-btn yp-btn-ghost !py-2 !px-4 text-sm" onClick={()=>setConfirm(null)} data-testid="summary-cancel-btn">{t("events.summary_cancel")}</button>
          </div>
        </div>
      </div>)}
  </div></Layout>);
}


function AttendeesModal({ regs, t, lang, onClose }) {
  const SL = { confirmed: t("att.confirmed"), cancelled: t("att.cancelled"), waitlist: t("att.waitlist") };
  const dt = (v) => (v ? new Date(v).toLocaleString(lang) : "—");
  const checkinTxt = (r) => (r.checked_in ? `${t("att.yes")}${r.checked_in_at ? ` (${dt(r.checked_in_at)})` : ""}` : t("att.no"));
  const Tel = ({ p }) => (p ? <a href={`tel:${p}`} className="text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="attendee-call"><Phone className="w-3.5 h-3.5" />{p}</a> : <span>—</span>);
  return (
    <div className="fixed inset-0 z-[1300] flex justify-center items-end sm:items-center p-0 sm:p-4" data-testid="attendees-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[85vh] flex flex-col shadow-xl sm:rounded-2xl" role="dialog" aria-modal="true">
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white sm:rounded-t-2xl">
          <h3 className="font-bold text-lg truncate">{t("att.title")} ({regs.length})</h3>
          <button onClick={onClose} className="w-11 h-11 -mr-1 flex items-center justify-center rounded-full hover:bg-slate-100" aria-label="X" data-testid="attendees-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {regs.length === 0 && <div className="text-slate-400 text-sm">{t("att.none")}</div>}
          {regs.length > 0 && (<>
            <table className="hidden sm:table w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2 pr-3">{t("att.name")}</th><th className="py-2 px-3">{t("att.surname")}</th>
                <th className="py-2 px-3">{t("att.taxid")}</th><th className="py-2 px-3">{t("att.age")}</th>
                <th className="py-2 px-3">{t("att.residence")}</th><th className="py-2 px-3">{t("att.phone")}</th>
                <th className="py-2 px-3">{t("att.email")}</th><th className="py-2 px-3">{t("att.status")}</th>
                <th className="py-2 px-3">{t("att.checkin")}</th><th className="py-2 px-3">{t("att.slot")}</th>
                <th className="py-2 pl-3">{t("att.registered")}</th>
              </tr></thead>
              <tbody>{regs.map((r, i) => (
                <tr key={i} className="border-b last:border-0 align-top" data-testid="attendee-row">
                  <td className="py-2 pr-3">{r.first_name || "—"}</td><td className="py-2 px-3">{r.last_name || "—"}</td>
                  <td className="py-2 px-3">{r.tax_id || "—"}</td><td className="py-2 px-3">{r.age || "—"}</td>
                  <td className="py-2 px-3">{r.residence || "—"}</td><td className="py-2 px-3"><Tel p={r.phone} /></td>
                  <td className="py-2 px-3 break-all">{r.email || "—"}</td>
                  <td className="py-2 px-3">{SL[r.status] || r.status}</td>
                  <td className="py-2 px-3">{checkinTxt(r)}</td>
                  <td className="py-2 px-3 whitespace-nowrap">{dt(r.slot_start)}</td>
                  <td className="py-2 pl-3 whitespace-nowrap">{dt(r.registered_at)}</td>
                </tr>))}</tbody>
            </table>
            <div className="sm:hidden space-y-3">
              {regs.map((r, i) => (
                <div key={i} className="yp-card p-3 text-sm" data-testid="attendee-card">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{r.first_name} {r.last_name}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 shrink-0">{SL[r.status] || r.status}</span>
                  </div>
                  <dl className="mt-2 space-y-1 text-xs text-slate-600">
                    <Field k={t("att.taxid")} v={r.tax_id} /><Field k={t("att.age")} v={r.age} />
                    <Field k={t("att.residence")} v={r.residence} /><Field k={t("att.email")} v={r.email} />
                    <div className="flex justify-between gap-3"><dt className="text-slate-400">{t("att.phone")}</dt><dd className="text-right"><Tel p={r.phone} /></dd></div>
                    <Field k={t("att.checkin")} v={checkinTxt(r)} /><Field k={t("att.slot")} v={dt(r.slot_start)} />
                    <Field k={t("att.registered")} v={dt(r.registered_at)} />
                  </dl>
                </div>))}
            </div>
          </>)}
        </div>
      </div>
    </div>);
}
const Field = ({ k, v }) => (<div className="flex justify-between gap-3"><dt className="text-slate-400 shrink-0">{k}</dt><dd className="text-right break-words min-w-0">{v || "—"}</dd></div>);
